import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Feather } from "lucide-react";

const MAX = 24000;

export default function StyleBriefDialog({ open, onOpenChange, project, onSave }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setText(project?.style_brief || ""); }, [open, project?.style_brief]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave({ style_brief: text.trim() });
      toast.success(text.trim() ? "Style brief saved — every draft, rewrite and polish now follows it" : "Style brief cleared");
      onOpenChange(false);
    } catch { toast.error("Couldn't save the style brief"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-2xl" data-testid="style-brief-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Feather className="w-5 h-5 text-primary" /> Style &amp; story brief</DialogTitle>
          <DialogDescription>
            Your own direction for this book — voice, characters, humour, what to avoid, planned scenes. It outranks the
            app's general style rules in every draft, rewrite and polish (Codex facts and continuity still win on facts).
          </DialogDescription>
        </DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value.slice(0, MAX))} rows={16}
          data-testid="style-brief-input" className="text-sm font-mono leading-relaxed"
          placeholder="e.g. First person, told by Dean. Dry, sweary British humour; the danger is real but the characters are funny. Bob is sarcastic and Welsh, drops the sarcasm when things get serious…" />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground" data-testid="style-brief-count">{text.length.toLocaleString()} / {MAX.toLocaleString()} characters</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="gap-2" data-testid="style-brief-save">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save brief
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
